<script lang="ts">// Internal component shared by Chart and DefaultChart: mounts the chart via
// `create` into the div below, pushes prop changes through the chart handle,
// and destroys the chart when the component is destroyed.
import { getAllContexts, onMount } from "svelte";
import { mountChartHost } from "./host";
let { create, class: className = undefined, style = undefined, dataTestId = undefined, ...chartProps } = $props();
let container;
let host = null;
let syncedProps = {};
function sameProps(a, b) {
	const aKeys = Object.keys(a);
	return aKeys.length === Object.keys(b).length && aKeys.every((key) => Object.is(a[key], b[key]));
}
/** Re-read the current config/data without new references (see Chart/DefaultChart). */
export function refresh() {
	host?.refresh();
}
// captured at init time: placeholders mount with this component's contexts
const componentContext = getAllContexts();
onMount(() => {
	syncedProps = { ...chartProps };
	host = mountChartHost(create, container, syncedProps, componentContext);
	return () => {
		const current = host;
		host = null;
		current?.destroy();
	};
});
// one style string, size declarations after the host's: removing an explicit size then restores the host's own width or height
const containerStyle = $derived([
	style,
	typeof chartProps.width === "number" ? `width: ${chartProps.width}px` : null,
	typeof chartProps.height === "number" ? `height: ${chartProps.height}px` : null
].filter(Boolean).join("; ") || undefined);
$effect(() => {
	// Spreading reads every chart prop so this effect tracks them all; comparing
	// (not run-counting) keeps a change made before the first run, e.g. in a
	// parent's onMount, from being dropped.
	const next = { ...chartProps };
	if (host === null || sameProps(next, syncedProps)) {
		return;
	}
	syncedProps = next;
	host.update(next);
});
</script>

<div
  bind:this={container}
  class={className}
  style={containerStyle}
  data-testid={dataTestId}
></div>
